'use client';
import {
  CommentItem,
  ContextUserType,
  IMessage,
  ReplyFormPortal,
  useMessageContext,
} from '@ui/components/organisms';
import { CommentOrReplyForm } from './comment-or-reply-form';
import { useEffect, useId, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { LoadingSkeleton } from '@ui/components';
import { CommentItemSkeleton } from './comment-item-skeleton';
import { ContentType } from '@ui/constants/context';

interface Props {
  userType: ContextUserType;
  post: IMessage;
  creator_id: string;
  user_id: string;
  main_post_ref: string;
  enableReply: boolean;
  contentType: ContentType;
  isDynamicRoute?: boolean;
  filter_by_comment_id?: string;
  filter_by_reply_id?: string;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
  moderators_ids: string[];
}
export const CommentList = (props: Props) => {
  const messageState = useMessageContext(props.userType);
  const id = useId();
  const initializedThreadRef = useRef<string | null>(null);

  useEffect(() => {
    messageState.onSetThread(props.post.child_thread_id);
    messageState.onSetCreator(props.creator_id);
    messageState.onSetConversation(props.post.conversation_id);
    messageState.onSetFan(props.user_id);
    if (props.filter_by_comment_id) {
      messageState.onSetFilterByMessage(props.filter_by_comment_id);
    }

    // Only reset page to 1 if this is a new thread
    if (initializedThreadRef.current !== props.post.child_thread_id) {
      messageState.onSetPage(1);
      initializedThreadRef.current = props.post.child_thread_id;
    }
  }, [
    props.post.child_thread_id,
    props.creator_id,
    props.post.conversation_id,
    props.user_id,
    props.filter_by_comment_id,
    props.filter_by_reply_id,
  ]);

  if (messageState.isLoading && messageState.messages.length === 0) {
    return (
      <div className="flex flex-col gap-y-6 pt-4">
        <LoadingSkeleton className=" m-w-[70vw] h-7 w-full rounded-sm" />
        <CommentItemSkeleton />
        <CommentItemSkeleton />
      </div>
    );
  }

  return (
    <>
      <ReplyFormPortal
        {...props}
        queryParams={messageState.query}
        community_details={
          props.post.community_details as NonNullable<IMessage['community_details']>
        }
        creatorData={props.creatorData}
      />
      <div className="space-y-4 mb-4">
        {!props.filter_by_comment_id && (
          <CommentOrReplyForm
            creator_id={props.post.creator_id}
            thread_id={props.post.child_thread_id}
            user_id={messageState.user_id}
            contentType={props.contentType === 'comment' ? 'reply' : 'comment'}
            userType={props.userType}
            activateToolbarOnFocus
            createThreadIfNotExist
            message_id={props.post.id}
            conversation_id={props.post.conversation_id}
            queryParam={messageState.query}
            main_post_ref={props.main_post_ref}
            community_details={
              props.post.community_details as NonNullable<IMessage['community_details']>
            }
            creatorData={props.creatorData}
          />
        )}

        {messageState.pinned_messages?.length > 0 && (
          <div className="mb-4 space-y-2 flex items-start flex-col w-full border-b border-b-orange-100 relative pb-5">
            {messageState.pinned_messages.map((comment) => (
              <div key={comment.id} className="relative w-full">
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  userType={props.userType}
                  user_id={messageState.user_id}
                  creator_id={props.creator_id || comment.creator_id}
                  enableReply={props.enableReply}
                  reportLabel="Report comment"
                  isReply={false}
                  query={messageState.query}
                  main_post_ref={props.main_post_ref}
                  isPinned
                  communityDetails={messageState.community_details || props.post.community_details}
                  isAuthenticated={messageState.isAuthenticated}
                  isFanOfCreator={messageState.isFanOfCreator}
                  hierarchyType="comment"
                  {...(props.filter_by_reply_id && {
                    filter_by_reply_id: props.filter_by_reply_id,
                    keepThreadLineOpen: true,
                  })}
                  moderators_ids={props.moderators_ids || []}
                />
              </div>
            ))}
          </div>
        )}

        <div className="pb-10">
          <InfiniteScroll
            className="scrollbar-none space-y-2"
            dataLength={messageState.messages.length}
            next={messageState.onNextPage}
            hasMore={messageState.hasMorePages}
            scrollableTarget={id}
            loader={
              <div className="py-4">
                <CommentItemSkeleton />
              </div>
            }>
            {messageState.messages.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                userType={props.userType}
                user_id={messageState.user_id}
                // creator_id={comment.creator_id || props.creator_id}
                creator_id={props.creator_id || comment.creator_id}
                enableReply={props.enableReply}
                reportLabel="Report comment"
                isReply={false}
                query={messageState.query}
                main_post_ref={props.main_post_ref}
                isDynamicRoute={props.isDynamicRoute}
                communityDetails={messageState.community_details || props.post.community_details}
                isAuthenticated={messageState.isAuthenticated}
                isFanOfCreator={messageState.isFanOfCreator}
                hierarchyType="comment"
                {...(props.filter_by_reply_id && {
                  filter_by_reply_id: props.filter_by_reply_id,
                  keepThreadLineOpen: true,
                })}
                moderators_ids={props.moderators_ids || []}
              />
            ))}
            {messageState.isLoading && (
              <div className="flex flex-col gap-y-6 pt-4">
                <LoadingSkeleton className=" m-w-[70vw] h-7 w-full rounded-sm" />
                <CommentItemSkeleton />
                <CommentItemSkeleton />
              </div>
            )}
          </InfiniteScroll>
        </div>
      </div>
    </>
  );
};
